import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Fade,
  Button
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const AttendeeDisplay = ({ eventId, refreshTrigger }) => {
  
  const [registrationData, setRegistrationData] = useState(null);
  const [userHasRSVPed, setUserHasRSVPed] = useState(false);
  const [userOptedToShare, setUserOptedToShare] = useState(false);
  const [showAttendeeList, setShowAttendeeList] = useState(false);

  useEffect(() => {
    if (!eventId) return;

    // Check user's RSVP status from localStorage
    const rsvpStatus = localStorage.getItem(`rsvp_status_${eventId}`);
    setUserHasRSVPed(rsvpStatus === 'true');
    
    // Check if user opted to share name
    const shareStatus = localStorage.getItem(`rsvp_share_${eventId}`);
    setUserOptedToShare(shareStatus === 'true');
    
    // Fetch registration data - if it fails, just don't show the feature
    const loadRegistrationData = async () => {
      try {
        const response = await fetch(`https://xnmboqwiuc.execute-api.us-east-1.amazonaws.com/rsvp?eventId=${eventId}`);
        
        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }
        
        const data = await response.json();
        setRegistrationData(data);
      } catch (err) {
        console.error('Failed to fetch registration data:', err);
        // Don't set error state - just leave registrationData as null
        // This will cause the component to not render anything
      }
    };
    
    loadRegistrationData();
  }, [eventId, refreshTrigger]);

  // No registration data - don't show the feature
  if (!registrationData) {
    return null;
  }

  // Determine display logic based on user RSVP status and sharing preference
  const canViewAttendeeList = userHasRSVPed && userOptedToShare;
  const hiddenCount = registrationData.totalCount - registrationData.publicCount;

  // Helper function to get count text with minimum 10 logic
  const getCountText = (count) => {
    const displayCount = Math.max(count, 10);
    if (displayCount === 0) return 'No one has registered yet';
    if (displayCount === 1) return '1 person has registered';
    return `${displayCount} people have registered`;
  };

  // Helper function to get attendee list display
  const renderAttendeeList = () => {
    if (!registrationData.attendees || registrationData.attendees.length === 0) {
      return (
        <Box sx={{ 
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #e0e0e0',
          mt: 2
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              fontStyle: 'italic',
              textAlign: 'center'
            }}
          >
            No attendees have opted to share their names yet.
          </Typography>
        </Box>
      );
    }

    const displayTotalCount = Math.max(registrationData.totalCount, 10);
    const anonymousCount = displayTotalCount - registrationData.publicCount;

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ 
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #e0e0e0'
        }}>
          {registrationData.attendees
            .filter(attendee => attendee.isPublic)
            .map((attendee, index) => (
            <Typography 
              key={index} 
              variant="body2" 
              sx={{ 
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.9rem'
              }}
            >
              <PersonIcon sx={{ mr: 1, fontSize: '1rem', opacity: 0.6 }} />
              {attendee.name}
            </Typography>
          ))}
          
          {anonymousCount > 0 && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontStyle: 'italic', 
                mt: 1.5,
                pt: 1.5,
                borderTop: '1px solid #e0e0e0',
                color: 'text.secondary',
                fontSize: '0.85rem'
              }}
            >
              ... and {anonymousCount} {anonymousCount === 1 ? 'other' : 'others'}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  // Helper function to render count display with optional expand button
  const renderCountDisplay = () => {
    const displayCount = Math.max(registrationData.totalCount, 10);
    
    return (
      <Box sx={{ py: 1 }}>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Typography 
            variant="body1" 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            <PeopleIcon sx={{ mr: 1, fontSize: '1.2rem', opacity: 0.7 }} />
            {getCountText(registrationData.totalCount)}
          </Typography>
          
          {/* Show expand button only for users who can view the list */}
          {canViewAttendeeList && displayCount > 0 && (
            <Button
              onClick={() => setShowAttendeeList(!showAttendeeList)}
              sx={{ 
                textTransform: 'none',
                fontSize: '0.85rem',
                color: 'primary.main',
                padding: '4px 8px',
                minWidth: 'auto'
              }}
              startIcon={showAttendeeList ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {showAttendeeList ? 'Hide list' : 'See who\'s coming'}
            </Button>
          )}
        </Box>
        
        {/* Show helper text only for non-RSVP'd users */}
        {!userHasRSVPed && displayCount > 0 && (
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 0.5,
              ml: 3,
              color: 'text.secondary',
              fontSize: '0.85rem',
              fontStyle: 'italic'
            }}
          >
            RSVP and opt to share your name to see who else is coming
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Fade in={true}>
      <Box sx={{ my: 2 }}>
        {renderCountDisplay()}
        {canViewAttendeeList && showAttendeeList && renderAttendeeList()}
      </Box>
    </Fade>
  );
};

export default AttendeeDisplay;